import { Buffer } from "buffer";
import {
  initialize,
  register,
  reset,
  identifyUser,
  createProfile,
  trackEvent,
} from "@sbd-ctg/user-behavior-tracking";

/**
 * FabPro / React SPA origin for post-login redirect.
 * REACT_APP_FAB is canonical; REACT_APP_FABPRO is accepted for local .env parity with fab_pro_web.
 * @returns {string}
 */
export function resolveFabBase() {
  const fab = process.env.REACT_APP_FAB || process.env.REACT_APP_FABPRO || "";
  return String(fab).replace(/\/+$/, "");
}

/**
 * JWT handoff path on the Fab origin. Always login.php (same as pre-SSO password login).
 * @returns {string}
 */
export function resolveFabTokenHandoff() {
  const configured = (
    process.env.REACT_APP_FAB_SSO_TOKEN_HANDOFF || "login.php"
  ).trim();
  if (configured === "root" || configured === "/") {
    return "login.php";
  }
  return configured || "login.php";
}

/**
 * Build the URL that hands off the API-issued JWT to FabPro via login.php.
 * @param {string} token
 * @returns {string}
 */
export function buildFabTokenHandoffUrl(token) {
  const base = resolveFabBase();
  const jwt = String(token || "").replace(/^"|"$/g, "");
  const encoded = encodeURIComponent(jwt);
  const handoff = resolveFabTokenHandoff();

  if (!base) {
    return `/login.php?token=${encoded}`;
  }

  const path = handoff.startsWith("/") ? handoff : `/${handoff}`;
  return `${base}${path}?token=${encoded}`;
}

/**
 * Redirect to FabPro with JWT after successful login (password or SSO handoff).
 * @param {string} token
 */
export function redirectToFabWithToken(token) {
  window.location.assign(buildFabTokenHandoffUrl(token));
}

/**
 * MixPanel tracking + FabPro redirect (shared by password and SSO flows).
 * @param {string} token
 */
export async function completeLoginWithToken(token) {
  const decodedToken = JSON.parse(
    Buffer.from(token.split(".")[1], "base64").toString("ascii")
  );
  const features = decodedToken.data.features
    ? decodedToken.data.features
    : null;

  const userBehaviorToken = `${process.env.REACT_APP_USER_BEHAVIOR_TRACKING_TOKEN}`;
  if (userBehaviorToken) {
    initialize(userBehaviorToken);
    register({
      product: "msuite fab",
      version: "2.5",
      application: "web",
    });
  }

  if (Array.isArray(features) && features.includes(44)) {
    const username = decodedToken.data.username;
    const name = username?.split("@")[0];
    reset();
    identifyUser(username);
    createProfile({ $email: username, $name: name });
    trackEvent("Web Login", {
      event_name: "Web Login",
      user_name: username ?? "",
      status: "Success",
      description: "Successful web login event occurred.",
    });
  }

  redirectToFabWithToken(token);
}
